import { useState } from 'react';
import { neighborhoodAvailable, neighborhoodProgress } from '@/core/domain/neighborhood';
import type { NeighborhoodStoryId } from '@/core/content/schemas/neighborhood';
import type { TaskCard } from '@/ui/hooks/useTaskCards';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useSaveDerived, useServices } from '@/ui/services/ServicesContext';
import { ItemThumb } from '@/ui/art/ItemThumb';
import styles from './TasksSheet.module.css';
import storyStyles from './NeighborhoodCards.module.css';

export function NeighborhoodCards({ onGoTo }: { onGoTo: (target: NonNullable<TaskCard['goto']>) => void }) {
  const { t } = useI18n();
  const { content, dispatch } = useServices();
  const cards = useSaveDerived((s) =>
    s.scenes.includes('minimart')
      ? content.neighborhoodStories.map((story) => ({
          id: story.id,
          ...neighborhoodProgress(content, s, story),
          available: neighborhoodAvailable(s, story),
          supplies: story.supplies.map((line) => ({
            ...line,
            have: s.inventory.productStacks[line.itemId] ?? 0,
          })),
        }))
      : [],
  );
  const [ending, setEnding] = useState<{ id: NeighborhoodStoryId; stage: number } | null>(null);
  const [error, setError] = useState(false);
  return cards.map((card) => {
    const story = content.neighborhoodStories.find((s) => s.id === card.id);
    if (!story) return null;
    const step = story.steps[Math.min(card.stage, 3)];
    if (!step) return null;
    const items = [...content.items.values()].filter((i) => i.setId === story.setId);
    return (
      <li
        key={card.id}
        className={styles.card}
        data-testid={`neighborhood-${card.id}`}
        data-done={card.stage === 4 || undefined}
      >
        <span className={styles.step}>{t('neighborhood.label')}</span>
        <h3 className={storyStyles.title}>{t(story.titleKey)}</h3>
        <span className={styles.hint}>{t(story.speakerKey)}</span>
        {!card.available ? (
          <p>{t('neighborhood.locked_elias')}</p>
        ) : (
          <>
            {ending?.id === card.id && (
              <p className={storyStyles.ending} role="status">
                {t(story.steps[ending.stage]?.endingKey ?? '')}
              </p>
            )}
            {card.stage < 4 ? (
              <>
                <strong>
                  {t('neighborhood.step', { current: card.stage + 1, total: 4 })} · {t(step.titleKey)}
                </strong>
                <p className={storyStyles.body}>{t(step.bodyKey)}</p>
                <p className={styles.hint}>{t(`neighborhood.goal.${card.stage}`)}</p>
                {card.stage === 1 && (
                  <ul className={storyStyles.supplies}>
                    {card.supplies.map((line) => (
                      <li key={line.itemId}>
                        {t(content.items.get(line.itemId)?.nameKey ?? '')}: {Math.min(line.have, line.qty)}/
                        {line.qty}
                      </li>
                    ))}
                  </ul>
                )}
                <span className={styles.count}>
                  {t('ui.tasks.progress', { current: card.current, target: card.target })}
                </span>
                <div className={storyStyles.rewards} aria-label={t('neighborhood.rewards')}>
                  <span>{t('neighborhood.rewards')}</span>
                  {step.rewards.map((reward, i) =>
                    reward.type === 'item' ? (
                      <span key={i} className={storyStyles.rewardItem}>
                        <span className={storyStyles.thumb}>
                          <ItemThumb itemId={reward.itemId} />
                        </span>
                        {t(content.items.get(reward.itemId)?.nameKey ?? '')}
                      </span>
                    ) : reward.type === 'coins' ? (
                      <span key={i}>{t('ui.reward.coins', { count: reward.amount })}</span>
                    ) : null,
                  )}
                </div>
                <div className={styles.actions}>
                  {!card.ready && (
                    <button
                      type="button"
                      className={styles.go}
                      onClick={() => {
                        const target: NonNullable<TaskCard['goto']> =
                          card.stage === 0
                            ? { room: 'minimart', panel: 'tasks' }
                            : card.stage === 3
                              ? { room: 'minimart' }
                              : card.stage === 1
                                ? { room: 'minimart', panel: 'stock' }
                                : { room: 'home' };
                        onGoTo(target);
                      }}
                    >
                      {t(`neighborhood.go.${card.stage}`)}
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.claim}
                    disabled={!card.ready}
                    data-testid={`neighborhood-advance-${card.id}`}
                    onClick={() => {
                      const result = dispatch({
                        type: 'advanceNeighborhood',
                        storyId: card.id,
                        stage: card.stage,
                      });
                      setError(!result.ok);
                      if (result.ok && !result.replayed) setEnding({ id: card.id, stage: card.stage });
                    }}
                  >
                    {t(`neighborhood.action.${card.stage}`)}
                  </button>
                </div>
              </>
            ) : (
              <p>{t('neighborhood.completed')}</p>
            )}
          </>
        )}
        <details className={storyStyles.collection}>
          <summary>{t('neighborhood.preview', { count: items.length })}</summary>
          <ul className={storyStyles.items}>
            {items.map((item) => (
              <li key={item.id}>
                <span className={storyStyles.thumb}>
                  <ItemThumb itemId={item.id} />
                </span>
                <span>{t(item.nameKey)}</span>
                {item.id === story.keepsakeItemId && <small>{t('neighborhood.keepsake')}</small>}
              </li>
            ))}
          </ul>
        </details>
        {card.stage === 4 && (
          <details>
            <summary>{t('neighborhood.reread')}</summary>
            {story.steps.map((s, i) => (
              <div key={i}>
                <h4>{t(s.titleKey)}</h4>
                <p>{t(s.bodyKey)}</p>
                <p>{t(s.endingKey)}</p>
              </div>
            ))}
          </details>
        )}
        {error && <p role="alert">{t('neighborhood.error')}</p>}
      </li>
    );
  });
}
